import { AppId, Button, FCX, ProductLogo } from "@datassential/platform-ui-lib";
import './SectionCharts.scss';
import FoodSection from "@pages/Food/components/Section/Section";
import FoodWidget from "@pages/Food/components/Widget/Widget";
import { iFoodProfileRecommendedCharts } from "@models/FoodProfileRecommendedCharts";
import cn from "classnames";
import { PermissionLevelEnum } from "@models/PermissionSection";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import { FoodSectionEnum } from "@pages/Food/Food";

const FoodSectionCharts: FCX<{
    data: iFoodProfileRecommendedCharts;
    word: string;
    isLoading: boolean;
    setHasData: (hasData: boolean) => void;
}> = ({
    data: {
        data,
        hasData,
        permissionLevel,
    },
    isLoading,
    word,
    setHasData,
}) => {
    const dispatch = useDispatch();
    const requestTrial: (f: string, hideNotification?: boolean) => any = (featureName, hideNotification) => {
        if (!hideNotification) {
            toast.info('Request has been sent');
        }
        return dispatch(requestTrialByFeature(featureName));
    };

    const [isExpanded, setIsExpanded] = useState(false);

    const hasAccess = permissionLevel !== PermissionLevelEnum.None;

    useEffect(() => {
        setHasData(!isLoading && data.length > 0);
    }, [isLoading, data.length, setHasData]);

    if (isLoading) {
        return (
            <div className="Food__section is-loading">
                <div className="spinner spinner-blink">
                    <ProductLogo appId={AppId.Snap} type="regular"/>
                </div>
            </div>
        );
    }

    if (data.length === 0) return null;

    return (
        <div
            className="Food__section" id={FoodSectionEnum.Charts}
            data-testid="blockFoodSectionCharts"
        >
            <FoodSection
                data-testid="blockFoodCharts"
                headerContent={(
                    <div data-testid="textFoodChartsTitle">
                        CHARTS
                    </div>
                )}
                className="appears-top del-2"
            >
                <FoodWidget className={cn("FoodSectionCharts", !hasAccess && 'is-blocked')}>
                    <div className={cn(
                        "FoodSectionCharts__content",
                        !isExpanded && 'is-collapsed',
                        "FoodSectionCharts__blur",
                    )}>
                        {data.map((item, index) => (
                            <Link
                                to={item.link}
                                key={item.title}
                                className="FoodSectionCharts__item"
                                title={`${item.title} (${item.typeLabel})`}
                                data-testid={`buttonFoodChartsItemLink-${index}`}
                            >
                                <div
                                    className="FoodSectionCharts__item-title"
                                    data-testid={`textFoodChartsItemTitle-${index}`}
                                >
                                    {item.title}
                                </div>
                                <div
                                    className="FoodSectionCharts__item-subtitle"
                                    data-testid={`textFoodChartsItemSubtitle-${index}`}
                                >
                                    ({item.typeLabel})
                                </div>
                            </Link>
                        ))}
                    </div>
                    <div className="FoodSectionCharts__footer">
                        {data.length > 14 && (
                            <Button
                                modifiers={['no-rem']}
                                onClick={() => setIsExpanded(!isExpanded)}
                                className="FoodSectionCharts__button"
                                data-testid="buttonFoodChartsToggleMore"
                            >
                                {isExpanded ? 'Show less' : 'Show more'}
                            </Button>
                        )}
                    </div>
                    {!hasAccess && (
                        <PermissionOverlay
                            onContactUsClick={() => requestTrial('mtd')}
                            data-testid="blockFoodChartsPermissionOverlay"
                        >
                            <div data-testid="textFoodChartsPermissionOverlayContentLine-0">
                                Upgrade your subscription to unlock
                            </div>
                            <div data-testid="textFoodChartsPermissionOverlayContentLine-1">
                                analyses featuring <b>{word}</b>
                            </div>
                        </PermissionOverlay>
                    )}
                </FoodWidget>
            </FoodSection>
        </div>
    );
};

export default FoodSectionCharts;